import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCcw, Clock, Shield, CheckCircle, XCircle } from 'lucide-react';

export const ReturnsExchanges = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Link 
              to="/" 
              className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Returns & Exchanges</h1>
            <p className="text-gray-600">Our hassle-free return and exchange policy</p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Return Policy</h2>
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <h3 className="text-lg font-semibold text-green-800">30-Day Return Policy</h3>
                </div>
                <p className="text-green-700">
                  We offer a 30-day return policy for all unused items in original packaging. 
                  Your satisfaction is our priority!
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Cancellation Policy</h2>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-6">
                <div className="flex items-center mb-3">
                  <XCircle className="h-6 w-6 text-orange-600 mr-3" />
                  <h3 className="text-lg font-semibold text-orange-800">Order Cancellation</h3>
                </div>
                <div className="text-orange-700 space-y-3">
                  <p>
                    <strong>Orders can be cancelled within 12 hours of placing the order.</strong>
                  </p>
                  <p>
                    Once your order has been processed or shipped, cancellations are not possible.
                  </p>
                  <p className="text-sm">
                    To cancel your order, please contact us immediately at{' '}
                    <a href="tel:+917094974571" className="font-semibold underline">+91 7094974571</a>
                    {' '}or email{' '}
                    <a href="mailto:contact@nineeyeshop.com" className="font-semibold underline">contact@nineeyeshop.com</a>
                    {' '}with your order number.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Return Conditions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                    Eligible for Return
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Items in original condition</li>
                    <li>• Original tags attached</li>
                    <li>• Unused and unworn</li>
                    <li>• Within 30 days of delivery</li>
                    <li>• Original packaging included</li>
                  </ul>
                </div>
                <div className="border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <RotateCcw className="h-5 w-5 text-red-600 mr-2" />
                    Not Eligible
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Used or worn items</li>
                    <li>• Items without original tags</li>
                    <li>• Custom or personalized items</li>
                    <li>• Items damaged by customer</li>
                    <li>• Beyond 30 days</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Return</h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">1</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Contact Us</h3>
                    <p className="text-gray-700">Email us at contact@nineeyeshop.com or call +91 7094974571</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">2</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Get Return Authorization</h3>
                    <p className="text-gray-700">We'll provide you with a return authorization number</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">3</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Package Items</h3>
                    <p className="text-gray-700">Pack items in original packaging with all tags attached</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">4</div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Ship Back</h3>
                    <p className="text-gray-700">Ship the package to our return address</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Exchange Policy</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Size Exchanges</h3>
                <p className="text-blue-700 mb-4">
                  Need a different size? We offer free size exchanges within 30 days of delivery.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Exchange Process</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Contact us for exchange request</li>
                      <li>• We'll send the new size</li>
                      <li>• Return the original item</li>
                      <li>• No additional charges</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-800 mb-2">Available Sizes</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• XS, S, M, L, XL, XXL</li>
                      <li>• 28, 30, 32, 34, 36, 38</li>
                      <li>• Subject to availability</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Refund Process</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Processing Time</h3>
                  <p className="text-sm text-gray-700">5-7 business days after we receive your return</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Refund Method</h3>
                  <p className="text-sm text-gray-700">Refunded to original payment method</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Confirmation</h3>
                  <p className="text-sm text-gray-700">You'll receive email confirmation once processed</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Return Shipping</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-yellow-800 mb-3">Shipping Costs</h3>
                <p className="text-yellow-700 mb-4">
                  Return shipping costs are the customer's responsibility unless the item was defective 
                  or we made an error in your order.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-2">Customer Pays</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Size exchanges</li>
                      <li>• Change of mind returns</li>
                      <li>• Wrong size ordered</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-2">We Pay</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Defective items</li>
                      <li>• Wrong item sent</li>
                      <li>• Our shipping errors</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                Need help with returns or exchanges? Our customer service team is here to help:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  <strong>Email:</strong> contact@nineeyeshop.com<br />
                  <strong>Phone:</strong> +91 7094974571<br />
                  <strong>Address:</strong> 1st Floor, 36-07, Kamaraj Colony 2nd Street, Kodambakkam, Chennai, Chennai, Tamil Nadu, 600024
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
